<script lang="ts">
export type OnDestroy = (id: string) => Promise<boolean>
</script>

<script lang="ts" setup>
import { Temporal } from '@js-temporal/polyfill'
import { sortBy } from '@std/collections'
import { provideApolloClient } from '@vue/apollo-composable'
import type { SetOptional } from 'type-fest'
import TaskTemplateSelect from './TaskTemplateSelect.vue'
import { useCurrentUser } from '@/src/hooks'
import { useBoolean } from '@/src/hooks/useBoolean'
import type { MyTaskTemplate } from '@/src/hooks/useMyTaskTemplates'
import { useMyTaskTemplates } from '@/src/hooks/useMyTaskTemplates'
import { getApolloClient } from '@/src/lib/apollo'
import { DEFAULT_WORK_DURATION_MINUTES } from '@/src/lib/constants'
import { ceilToTimelineInterval, JST_TIMEZONE } from '@/src/lib/dateTimeUtils'
import { assertIsDefined } from '@/src/lib/typeUtils'

type WorkValues = {
  id?: string
  from: string
  to: string
  taskTemplateId?: string | undefined
  burst: boolean
  note: string
}

type OnCreate = (values: WorkValues) => Promise<boolean>
type OnUpdate = (values: Required<Omit<WorkValues, 'taskTemplateId'>> & { taskTemplateId?: string }) => Promise<boolean>

const props = defineProps<{
  headerTitle: string
  // TODO: フォーム内のフィールド情報はまとめた方が都合良さそう
  id?: string
  from?: string
  to?: string
  taskTemplateId?: string
  burst?: boolean
  note?: string
  currentDate: string
  editableModel: string
  disabled?: boolean
  // TODO: onCreateとonUpdateはonSubmit一つに出来そう
  onCreate?: OnCreate
  onUpdate?: OnUpdate
  onDestroy?: OnDestroy
}>()

const emit = defineEmits<{
  close: []
}>()

provideApolloClient(getApolloClient())

const selectRef = ref<InstanceType<typeof TaskTemplateSelect>>()
const currentUser = useCurrentUser()

const { taskTemplates, loading } = useMyTaskTemplates()

const doneWork = reactive<SetOptional<WorkValues, 'taskTemplateId' | 'from' | 'to'>>({
  id: props.id,
  from: props.from,
  to: props.to,
  taskTemplateId: props.taskTemplateId,
  burst: props.burst ?? false,
  note: props.note ?? '',
})

const [isSubmitting, setIsSubmitting] = useBoolean(false)
const isNew = computed(() => !props.id)
const fromTime = computed(() => {
  const time = doneWork.from
    ? Temporal.PlainTime.from(doneWork.from)
    : ceilToTimelineInterval(Temporal.Now.plainTimeISO(JST_TIMEZONE))
  return time.toString({ smallestUnit: 'minute' })
})
const toTime = computed(() => {
  const time = doneWork.to
    ? Temporal.PlainTime.from(doneWork.to)
    : Temporal.PlainTime.from(fromTime.value).add({ minutes: DEFAULT_WORK_DURATION_MINUTES })
  return time.toString({ smallestUnit: 'minute' })
})

const sortedTaskTemplates = computed(() => {
  return sortBy(taskTemplates.value, ({ team, title }) =>
    [team.id === currentUser.mainTeamId ? 0 : 1, team.name, title].join('__'),
  )
})

const selectedTaskTemplate = computed<MyTaskTemplate | undefined>(() => {
  return taskTemplates.value.find(({ id }) => id === doneWork.taskTemplateId)
})
const isDoneWork = computed(() => props.editableModel === 'doneWork')
const canSubmit = computed(() => {
  // DoneWorkの場合はタスク必須
  if (isDoneWork.value) {
    return !!selectedTaskTemplate.value
  }
  // PlannedWorkの場合はタスク未選択でも登録・更新可能
  return true
})
const showFreeWordField = computed(() => {
  // タスクが選択されている、またはPlannedWork（タスク未選択でも入力可能）の場合に表示
  return !!selectedTaskTemplate.value || !isDoneWork.value
})

const close = (): void => {
  emit('close')
}

const handleDestroy = async (): Promise<void> => {
  if (!props.id || !props.onDestroy) return

  if (await props.onDestroy(props.id)) {
    close()
  }
}

const setFromTime = (value: string): void => {
  const from = Temporal.PlainTime.from(fromTime.value)
  const to = Temporal.PlainTime.from(toTime.value)
  const newFrom = Temporal.PlainTime.from(value)

  if (isNew.value) {
    const newTo = newFrom.add(from.until(to))
    // NOTE: 終了時間が23:45といった値の場合に開始時間計算結果が00:15といった値になった場合を考慮している
    doneWork.to = (Temporal.PlainTime.compare(newTo, newFrom) === -1 ? newFrom : newTo).toString()
  }
  doneWork.from = newFrom.toString()
}

const setToTime = (value: string): void => {
  doneWork.to = Temporal.PlainTime.from(value).toString()
}

const convertDateTimeString = (timeString: string): string => {
  return Temporal.PlainDate.from(props.currentDate).toPlainDateTime(timeString).toString()
}

const workParams = (): WorkValues => {
  const { taskTemplateId, burst } = doneWork

  return {
    ...doneWork,
    taskTemplateId,
    burst: burst ?? false,
    from: convertDateTimeString(fromTime.value),
    to: convertDateTimeString(toTime.value),
  }
}

const handleCreate = async (): Promise<void> => {
  if (isSubmitting.value || !props.onCreate) return

  setIsSubmitting.on()
  const success = await props.onCreate(workParams())
  setIsSubmitting.off()
  if (success === false) return
  close()
}

const handleUpdate = async (): Promise<void> => {
  assertIsDefined(props.id)
  if (isSubmitting.value || !props.onUpdate) return

  setIsSubmitting.on()
  const success = await props.onUpdate({
    ...workParams(),
    id: props.id,
  })
  setIsSubmitting.off()
  if (success === false) return
  close()
}

const focusToSelect = () => {
  nextTick(() => {
    selectRef.value?.focus()
  })
}

watch(loading, () => {
  if (isNew.value) {
    focusToSelect()
  }
})

onMounted(() => {
  if (isNew.value && !loading.value) {
    focusToSelect()
  }
})
</script>

<template>
  <form data-test-selector="work-form">
    <!-- TODO: モーダルでセンター寄せにする為に必要。モーダル含めたコンポーネントとして整理するタイミングで無くしたい-->
    <div class="card is-mx-auto">
      <header class="card-header">
        <p class="card-header-title">{{ headerTitle }}</p>
      </header>
      <div class="card-content">
        <div class="content">
          <div class="input-fields hstack tw:gap-3">
            <InputTime
              :model-value="fromTime"
              class="input-time"
              data-test-selector="from-time"
              :disabled="disabled"
              @update:modelValue="setFromTime"
            />
            <span class="tw:iconify tw:bi--arrow-right" />
            <InputTime
              :model-value="toTime"
              class="input-time"
              data-test-selector="to-time"
              :start-time="fromTime"
              :disabled="disabled"
              @update:modelValue="setToTime"
            />
            <div v-if="isDoneWork" class="field is-inline-flex">
              <label class="checkbox">
                <input v-model="doneWork.burst" type="checkbox" data-test-selector="burst" :disabled="disabled" />
                突発
              </label>
            </div>
          </div>
          <TaskTemplateSelect
            ref="selectRef"
            v-model="doneWork.taskTemplateId"
            class="task-template-select"
            :loading="loading"
            :task-templates="sortedTaskTemplates"
            :disabled="disabled"
            :clearable="!isDoneWork"
          />
          <section v-if="selectedTaskTemplate" class="mt-5">
            <div class="field">
              <label class="label is-size-7 mb-0"> チーム名 </label>
              <div class="control">
                <input
                  class="input is-static"
                  type="text"
                  :value="selectedTaskTemplate.team.name"
                  tabindex="-1"
                  readonly
                />
              </div>
            </div>
            <div class="field">
              <label class="label is-size-7 mb-0"> 行動項目 </label>
              <div class="control">
                <input
                  class="input is-static"
                  type="text"
                  :value="selectedTaskTemplate.companyLargeCategory.name"
                  tabindex="-1"
                  readonly
                />
              </div>
            </div>
          </section>
          <section v-if="showFreeWordField" class="mt-5">
            <div class="field">
              <label class="label is-size-7 mb-0">
                フリーワード
                <span class="has-text-grey">※分析結果には含まれません</span>
              </label>
              <BInput v-model="doneWork.note" data-test-selector="note" :disabled="disabled" />
            </div>
          </section>
        </div>
      </div>
      <footer class="modal-card-foot">
        <BuButton
          v-if="isNew"
          class="px-6"
          type="is-primary"
          outlined
          :disabled="disabled || !canSubmit || isSubmitting"
          @click="handleCreate"
        >
          登録
        </BuButton>
        <BuButton
          v-if="!isNew"
          class="px-6"
          type="is-primary"
          outlined
          :disabled="disabled || !canSubmit || isSubmitting"
          @click="handleUpdate"
        >
          更新
        </BuButton>
        <BuButton
          v-if="!isNew"
          class="is-ml-auto"
          type="is-danger"
          outlined
          :disabled="disabled || isSubmitting"
          @click="handleDestroy"
        >
          削除
        </BuButton>
      </footer>
    </div>
  </form>
</template>

<style scoped lang="scss">
@use '@/stylesheets/application/resources';

.card {
  max-width: 600px;

  @include resources.tablet {
    // NOTE: ガタつかないようにサイズ固定
    width: 550px;
  }
}

.card-content {
  height: 465px;
}

.input-fields {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.input-time {
  width: 80px;
}

// NOTE: VueSelectコンポーネント内の高さ調整の為
.task-template-select :deep(.vs__dropdown-menu) {
  max-height: 400px;
}
</style>
